import { Box, Button, Checkbox, Container, Flex, Heading, Text } from '@chakra-ui/react';
import { format } from 'date-fns';
import { Response } from 'datx-jsonapi';
import { IError } from 'datx-jsonapi/dist/interfaces/JsonApi';
import { useRouter } from 'next/router';
import { FC, useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorMessage } from '@/components/core/inputs/ErrorMessage';
import { Field } from '@/components/core/inputs/Field';
import { FadeInModalContext } from '@/components/layouts/fadeInModalContext';
import {
  AAPTSocialSignInButton,
  GoogleSocialSignInButton,
} from '@/components/ui/shared/social/SocialSignInButton/SocialSignInButton';
import { useAnalytics } from '@/context/Analytics';
import { AccessFrom } from '@/enums/AccessFrom';
import { IToastType } from '@/enums/ToastType';
import { useLoginMutation } from '@/hooks/useLoginMutation';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import useSession from '@/hooks/useSession';
import { useToast } from '@/hooks/useToast';
import { Session, SessionDocument } from '@/resources/Session';
import { User } from '@/resources/User';

import { OverlayType } from '../../../../../enums';

interface IFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

export const LogInFormSection: FC = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const [serverErrors, setServerErrors] = useState<Array<string>>([]);
  const toast = useToast();
  const context = useContext(FadeInModalContext);
  const { t } = useTranslation();
  const { mutateSession } = useSession();

  const router = useRouter();

  const analytics = useAnalytics();

  const { accessFrom = AccessFrom.Core } = useQueryParams<{ accessFrom: AccessFrom }>({
    accessFrom: StringParam,
  });

  const onLoginSuccess = async (user: User) => {
    analytics.identify(user.id, {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      createdAt: format(new Date(), 'yyyy-M-dd'),
    });

    analytics.track('user_login');

    toast({ status: IToastType.SUCCESS, title: 'Successfully logged in' });
  };

  const onLoginError = (loginErrors: Array<IError> | Error) => {
    if (loginErrors instanceof Array) {
      setServerErrors(loginErrors.map((error: IError) => error?.detail));
      toast({ status: IToastType.ERROR, title: loginErrors?.[0]?.detail });
    } else {
      setServerErrors([errors?.message?.toString() || 'Error']);
      toast({ status: IToastType.ERROR, title: errors?.message?.toString() || 'Error' });
    }
  };

  const [logIn] = useLoginMutation({
    onSuccess: ({ data }) => {
      const session = data.data as Session;

      onLoginSuccess(session?.user);
      // mutateSession because sessions/me has a different swr key
      mutateSession(data);
    },
    onFailure: ({ error }) => {
      if (error instanceof Response) {
        onLoginError(error?.error);
      }
    },
  });

  const onSubmit = async (formData: IFormData) => {
    const data: SessionDocument = {
      data: {
        type: 'sessions',
        id: '',
        attributes: {
          ...formData,
        },
      },
    };

    setServerErrors([]);
    logIn(data);
  };

  function handleCreateAccountClick() {
    analytics.track('user_account_create_click', { location: 'log_in' });
    router.push(`/create-account?accessFrom=${accessFrom}`);
  }

  return (
    <Container>
      <Box px={{ base: 5, md: 0 }} pb={{ base: 6, md: 0 }} pt={8} maxW={{ base: 'full', md: '360px' }} mx="auto">
        <Flex as="form" flexDirection="column" justifyContent="center" onSubmit={handleSubmit(onSubmit)}>
          <Heading fontSize={{ base: '2rem', md: '3rem' }} mb={12}>
            Log in
          </Heading>
          <Box mb={5}>
            <Field
              {...register('email', {
                required: 'Field is required',
              })}
              label="Email"
              hasValue={Boolean(watch('email'))}
              isError={Boolean(errors['email']) || serverErrors.length > 0}
            />
            <ErrorMessage errors={errors} name="email" />
          </Box>
          <Box mb={5}>
            <Field
              {...register('password', {
                required: 'Field is required',
              })}
              label="Your password"
              type="password"
              hasValue={Boolean(watch('password'))}
              isError={Boolean(errors['password']) || serverErrors.length > 0}
            />
            <ErrorMessage errors={errors} name="password" />
          </Box>

          <Checkbox size="lg" colorScheme="violet" defaultChecked {...register('rememberMe')}>
            Remember me
          </Checkbox>

          <Button type="submit" mt={{ base: 10, md: 14 }} mb={{ base: 4, md: 6 }}>
            log in
          </Button>

          <Button variant="unstyled" onClick={() => context.showModalType.set(OverlayType.RESET_PASSWORD)}>
            Forgot password?
          </Button>

          <Flex flexDirection="column" mt={{ base: 12, md: '3.75rem' }} mb={{ base: 8, md: 10 }}>
            <GoogleSocialSignInButton actionType="login">{t('googleLogin')}</GoogleSocialSignInButton>

            <AAPTSocialSignInButton css={{ marginTop: '10px' }}>{t('aaptLogin')}</AAPTSocialSignInButton>
          </Flex>

          <Flex whiteSpace="pre-wrap" mb={2} alignItems="center">
            <span>Don’t have an account? </span>
            <Button variant="unstyled" onClick={handleCreateAccountClick}>
              Create account
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Container>
  );
};
